import { TextField } from '@mui/material'
import { styled } from '@mui/system'
import { useGridFilter } from 'ag-grid-react'
import { intl } from '../../../../../../../../i18n'
import objects from '../../../../../../../../utils/objects'
import DateVO from '../../../../../../../../vo/DateVO'
import ResetButton from '../../../../../../../components/buttons/ResetButton'
import DateInputWithClassVO from '../../../../../../../components/editors/input/DateInput/DateInputWithClassVO'
import CommentCellDateOperatorSelect, {
  CommentCellDateFilterOperator,
  CommentCellDateFilterOperatorLabel,
} from './CommentCellDateOperatorSelect'
import CommentCellTextOperatorSelect, {
  CommentCellTextFilterOperator,
  CommentCellTextFilterOperatorLabel,
} from './CommentCellTextOperatorSelect'

type CommentFilterValue = {
  textValue?: string
  dateValue?: string
  textFilterOperator?: CommentCellTextFilterOperator
  dateFilterOperator?: CommentCellDateFilterOperator
}

type CommentCellFilterModel = {
  value: CommentFilterValue
}

const buildModel = (
  textValue?: string,
  dateValue?: DateVO,
  textFilterOperator?: CommentCellTextFilterOperator,
  dateFilterOperator?: CommentCellDateFilterOperator
): CommentCellFilterModel | null => {
  const isTextActive = !!textValue && !!textFilterOperator
  const isDateActive = !!dateValue && !!dateFilterOperator
  if (!isTextActive && !isDateActive) return null
  return {
    value: {
      textValue: textValue || undefined,
      dateValue: dateValue ? dateValue.toLabel() : undefined,
      textFilterOperator,
      dateFilterOperator,
    },
  }
}

const RootDiv = styled('div')({
  padding: '3px 8px',
  display: 'flex',
})
const StyledCommentCellDateOperatorSelect = styled(
  CommentCellDateOperatorSelect
)({
  minWidth: 35,
  margin: 'auto 5px auto 0',
})
const StyledCommentCellTextOperatorSelect = styled(
  CommentCellTextOperatorSelect
)({
  minWidth: 35,
  margin: 'auto 5px auto 0',
})
const StyledDateInputWithClassVO = styled(DateInputWithClassVO)({
  width: 110,
  margin: 'auto 5px auto 0',
  alignItems: 'center',
})
const StyledTextField = styled(TextField)({
  width: 105,
  marginRight: '10px !important',
  '& .MuiOutlinedInput-root': {
    '& .MuiInputBase-input': {
      padding: '0px !important',
    },
  },
})
const StyledResetButton = styled(ResetButton)({
  whiteSpace: 'nowrap',
  margin: 'auto 0',
})

const CommentCellFilterUI = ({
  key,
  textValue,
  dateValue,
  textFilterOperator,
  dateFilterOperator,
  onChangeTextValue,
  onChangeDateValue,
  onChangeTextFilterOperator,
  onChangeDateFilterOperator,
  clearTextFilter,
  clearDateFilter,
}: {
  key: string
  textValue?: string
  dateValue?: DateVO
  textFilterOperator?: CommentCellTextFilterOperator
  dateFilterOperator?: CommentCellDateFilterOperator
  onChangeTextValue: (event: React.ChangeEvent) => void
  onChangeDateValue: (dateValue?: DateVO) => void
  onChangeTextFilterOperator: (operator?: CommentCellTextFilterOperator) => void
  onChangeDateFilterOperator: (operator?: CommentCellDateFilterOperator) => void
  clearTextFilter: () => void
  clearDateFilter: () => void
}) => (
  <>
    <RootDiv>
      <StyledCommentCellDateOperatorSelect
        key={`${key}-${dateFilterOperator}`}
        value={dateFilterOperator}
        onChange={onChangeDateFilterOperator}
      />
      <StyledDateInputWithClassVO
        value={dateValue}
        onChange={onChangeDateValue}
      />
      <StyledResetButton
        label={intl.formatMessage({ id: 'clear' })}
        onClick={clearDateFilter}
      />
    </RootDiv>
    <RootDiv>
      <StyledCommentCellTextOperatorSelect
        key={`${key}-${textFilterOperator}`}
        value={textFilterOperator}
        onChange={onChangeTextFilterOperator}
      />
      <StyledTextField
        variant="outlined"
        size="small"
        onChange={onChangeTextValue}
        value={textValue}
      />
      <StyledResetButton
        label={intl.formatMessage({ id: 'clear' })}
        onClick={clearTextFilter}
      />
    </RootDiv>
  </>
)

interface Props {
  colDef: {
    field: string
  }
  model: CommentCellFilterModel | null
  onModelChange: (model: CommentCellFilterModel | null) => void
}

const MILLSECOND_PER_DAY = 24 * 60 * 60 * 1000

export default (props: Props) => {
  const textValue = props.model?.value?.textValue ?? ''
  const dateValue = props.model?.value?.dateValue
    ? new DateVO(props.model.value.dateValue)
    : undefined
  const textFilterOperator =
    props.model?.value?.textFilterOperator ??
    CommentCellTextFilterOperator.CONTAINS
  const dateFilterOperator =
    props.model?.value?.dateFilterOperator ?? CommentCellDateFilterOperator.GTE

  const doesFilterPass = params => {
    // Don't filter new rows
    if (params.data.isAdded) return true
    if (!props.model) return true
    const latestComment = objects.getValue(params.data, props.colDef.field)
    const createdAt = latestComment ? latestComment.createdAt : undefined
    const commentText = latestComment ? latestComment.text : undefined
    const rowDateValue = createdAt ? new Date(createdAt).getTime() : undefined
    const filterTextValue = props.model.value.textValue || undefined
    const filterDateValue = dateValue ? dateValue.toDate().getTime() : undefined

    let isTextFilterPass
    switch (filterTextValue && props.model.value.textFilterOperator) {
      case CommentCellTextFilterOperator.CONTAINS:
        isTextFilterPass = commentText && commentText.includes(filterTextValue)
        break
      case CommentCellTextFilterOperator.NOT_CONTAINS:
        isTextFilterPass = commentText && !commentText.includes(filterTextValue)
        break
      default:
        isTextFilterPass = true
    }

    let isDateFilterPass
    if (filterDateValue) {
      switch (props.model.value.dateFilterOperator) {
        case CommentCellDateFilterOperator.EQ:
          isDateFilterPass =
            rowDateValue &&
            filterDateValue &&
            filterDateValue <= rowDateValue &&
            rowDateValue < filterDateValue + MILLSECOND_PER_DAY
          break
        case CommentCellDateFilterOperator.GTE:
          isDateFilterPass =
            rowDateValue && filterDateValue && rowDateValue >= filterDateValue
          break
        case CommentCellDateFilterOperator.LTE:
          isDateFilterPass =
            rowDateValue && filterDateValue && rowDateValue <= filterDateValue
          break
        default:
          isDateFilterPass = true
      }
    } else {
      isDateFilterPass = true
    }
    return isTextFilterPass && isDateFilterPass
  }

  const getModelAsString = () => {
    if (!props.model) return ''
    const filterLabels: string[] = []
    if (dateValue && props.model.value.dateFilterOperator) {
      filterLabels.push(
        CommentCellDateFilterOperatorLabel[
          props.model.value.dateFilterOperator
        ] + ` : ${dateValue.toLabel()}`
      )
    }
    if (props.model.value.textValue && props.model.value.textFilterOperator) {
      filterLabels.push(
        CommentCellTextFilterOperatorLabel[
          props.model.value.textFilterOperator
        ] + ` : ${props.model.value.textValue}`
      )
    }
    return filterLabels.join(', ')
  }

  useGridFilter({ doesFilterPass, getModelAsString })

  return (
    <CommentCellFilterUI
      key={`comment-cell-filter-${props.colDef.field}`}
      textValue={textValue}
      dateValue={dateValue}
      textFilterOperator={textFilterOperator}
      dateFilterOperator={dateFilterOperator}
      onChangeTextFilterOperator={op =>
        props.onModelChange(
          buildModel(textValue, dateValue, op, dateFilterOperator)
        )
      }
      onChangeDateFilterOperator={op =>
        props.onModelChange(
          buildModel(textValue, dateValue, textFilterOperator, op)
        )
      }
      onChangeTextValue={event =>
        props.onModelChange(
          buildModel(
            (event.target as HTMLInputElement).value,
            dateValue,
            textFilterOperator,
            dateFilterOperator
          )
        )
      }
      onChangeDateValue={val =>
        props.onModelChange(
          buildModel(textValue, val, textFilterOperator, dateFilterOperator)
        )
      }
      clearTextFilter={() =>
        props.onModelChange(
          buildModel(
            '',
            dateValue,
            CommentCellTextFilterOperator.CONTAINS,
            dateFilterOperator
          )
        )
      }
      clearDateFilter={() =>
        props.onModelChange(
          buildModel(
            textValue,
            undefined,
            textFilterOperator,
            CommentCellDateFilterOperator.GTE
          )
        )
      }
    />
  )
}
